import { supabase } from './supabase';
import { StorageService } from './storage';
import type { AnalysisResult } from './ai-analysis';
import { broadcastCreditChange } from './credit-events';

export interface EnhancementOptions {
  customInstructions?: string;
  quality?: 'low' | 'medium' | 'high';
  maintainBranding?: boolean;
  analysisId?: string; // Optional analysis ID to link enhanced image
}

export interface EnhancementProgress {
  stage: 'analyzing' | 'generating' | 'uploading' | 'complete';
  progress: number;
  message: string;
}

export interface EnhancementResult {
  success: boolean;
  enhancedImageUrl?: string;
  enhancedImageId?: string;
  error?: string;
  creditDeducted: boolean;
}

export class AIImageEnhancementService {
  /**
   * Enhance an image creative using OpenAI's GPT Image-1 model
   */
  static async enhanceCreative(
    creativeId: string,
    creativeUrl: string,
    analysisData: AnalysisResult,
    options: EnhancementOptions = {},
    onProgress?: (progress: EnhancementProgress) => void
  ): Promise<EnhancementResult> {
    try {
      // Report initial progress
      onProgress?.({
        stage: 'analyzing',
        progress: 10,
        message: 'Analyzing creative and generating enhancement suggestions...'
      });

      // Note: Enhancement prompt is now generated dynamically in the edge function
      // using comprehensive analysis data including platforms, performance, and visual analysis

      onProgress?.({
        stage: 'generating',
        progress: 30,
        message: 'Generating enhanced creative with AI...'
      });

      // Call Supabase edge function for enhancement
      const { data: enhancementData, error: enhancementError } = await supabase.functions.invoke('enhance-creative', {
        body: {
          creativeId,
          creativeUrl,
          analysisData, // Complete analysis data including all platforms, performance, and visual analysis
          options: {
            quality: options.quality === 'low' ? 'standard' : 'high',
            maintainBranding: options.maintainBranding ?? true,
            customInstructions: options.customInstructions || ''
          },
          analysisId: options.analysisId // Pass analysis ID to edge function
        }
      });

      if (enhancementError || !enhancementData?.success) {
        console.error('Enhancement API error:', enhancementError || enhancementData);
        return {
          success: false,
          error: enhancementData?.error || 'Failed to enhance creative. Please try again.',
          creditDeducted: false
        };
      }

      onProgress?.({
        stage: 'complete',
        progress: 100,
        message: 'Enhancement complete!'
      });

      // Broadcast credit change event for real-time UI updates
      if (enhancementData.creditsUsed && enhancementData.creditsRemaining !== undefined) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          broadcastCreditChange(
            'credit_consumed',
            user.id,
            -enhancementData.creditsUsed,
            enhancementData.creditsRemaining,
            {
              reason: 'Creative Enhancement',
              transactionId: enhancementData.creativeId,
              enhancementId: enhancementData.analysisId
            }
          );
        }
      }

      return {
        success: true,
        enhancedImageUrl: enhancementData.enhancedImageUrl, // Now comes directly from OpenAI as URL
        enhancedImageId: undefined, // We're using the URL directly from OpenAI
        creditDeducted: true
      };

    } catch (error) {
      console.error('Error in enhanceCreative:', error);
      return {
        success: false,
        error: 'An unexpected error occurred during enhancement',
        creditDeducted: false
      };
    }
  }

  /**
   * Generate enhancement prompt based on analysis results
   * @deprecated This method is now replaced by server-side prompt generation in the edge function
   * which uses comprehensive analysis data for more targeted enhancements
   */
  private static generateEnhancementPrompt(
    analysisData: AnalysisResult,
    options: EnhancementOptions
  ): string {
    // This is a fallback method - actual prompt generation now happens in the edge function
    // using complete analysis data including platform compliance, performance scores, and visual analysis
    return 'Enhanced professional advertising creative with improved visual quality and compliance';
  }

  /**
   * Detect aspect ratio from analysis data or image metadata
   * @deprecated Now handled server-side in the edge function
   */
  private static detectAspectRatio(analysisData: AnalysisResult): string {
    // This is now handled in the edge function using the metadata from analysis
    return '1024x1024';
  }

  /**
   * Check if enhancement is available for the creative type
   */
  static isEnhancementAvailable(creativeType: string): boolean {
    return creativeType === 'image';
  }

  /**
   * Estimate enhancement time based on options
   */
  static estimateEnhancementTime(options: EnhancementOptions = {}): number {
    const baseTime = 20; // Base time in seconds
    const qualityMultiplier = options.quality === 'high' ? 1.5 : 1.0;
    
    return Math.round(baseTime * qualityMultiplier);
  }
}